import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { systemRolesRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute } from 'src/app/app.selectors';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { AddRoleComponent } from '../../add-role/containers/add-role.component';
import { RolesFilterOption } from '../models/role-filter-option';
import { RoleOrderingOptions, RoleRegisterOptions } from '../models/role-register-options';
import { RoleRegisterListItem, RoleRegisterResults } from '../models/role-register-results.model';
import { GetRoles, GetRoles_rolesConnection_items } from '../queries/generated/GetRoles';
import { RoleRegisterApiService } from '../services/role-register-api.service';
import {
   createRole,
   loadAccountRolesSuccess,
   loadSystemRolesSuccess,
   roleRegisterAdapterActions,
} from './role-register.actions';
import { selectEndCursor, selectOrderingOptions, selectQuickFilters } from './role-register.reducer';
import * as RoleRegisterSelectors from './role-register.selectors';
import { selectFilters } from './role-register.selectors';

@Injectable()
export class RoleRegisterEffects {
   addRole$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createRole),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddRoleComponent, {
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   loadRoleList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(roleRegisterAdapterActions.pageEnter),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectCurrentRoute)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(RoleRegisterSelectors.selectIsOnSystemPage))
         ),
         switchMap(([action, filters, quickFilters, route, orderingOptions, wasPreviouslyOnSystemPage]) => {
            const path = route.url[0].path;
            if (path === systemRolesRoute) {
               if (!wasPreviouslyOnSystemPage) {
                  this.store.dispatch(roleRegisterAdapterActions.clearRegisterFilters());
                  quickFilters = null;
               }
               this.store.dispatch(loadSystemRolesSuccess());
            } else {
               if (wasPreviouslyOnSystemPage) {
                  this.store.dispatch(roleRegisterAdapterActions.clearRegisterFilters());
                  quickFilters = null;
               }
               this.store.dispatch(loadAccountRolesSuccess());
            }
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   loadRolePage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(roleRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([action, filters, endCursor, orderingOptions, quickFilters]) => {
            return this.roleRegisterApiService.getRoles(endCursor, filters, quickFilters, orderingOptions).pipe(
               map((result) => {
                  return roleRegisterAdapterActions.loadPageSuccess(
                     this.convertGetRolesResultToLoadSuccessProps(result.data)
                  );
               }),
               catchError((error) => {
                  return of(roleRegisterAdapterActions.loadPageFailure(error));
               })
            );
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(roleRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(RoleRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickFilters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(roleRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickfilters, orderingOptions]) => {
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   dialogId: string;

   constructor(
      private actions$: Actions,
      private dialogService: LcsDialogService,
      private store: Store<AppState>,
      private roleRegisterApiService: RoleRegisterApiService
   ) {}

   private convertGetRolesResultToLoadSuccessProps(getRoles: GetRoles): RoleRegisterResults {
      return {
         endCursor: getRoles.rolesConnection.pageInfo.endCursor,
         hasNextPage: getRoles.rolesConnection.pageInfo.hasNextPage,
         items: getRoles.rolesConnection.items.map(this.convertRoleRegisterListItem),
         totalCount: getRoles.rolesConnection.totalCount,
      };
   }

   private convertRoleRegisterListItem(getRoleItem: GetRoles_rolesConnection_items): RoleRegisterListItem {
      return {
         accountName: getRoleItem.account?.name ?? null,
         description: getRoleItem.description,
         name: getRoleItem.name,
         roleId: getRoleItem.roleId,
         roleUserCount: getRoleItem.userRolesCount,
      };
   }

   private loadNewData(
      filters: RolesFilterOption[],
      quickFilters: RoleRegisterOptions | null = null,
      orderingOptions: RoleOrderingOptions[] | null = null
   ) {
      return this.roleRegisterApiService.getRoles(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return roleRegisterAdapterActions.loadSuccess(this.convertGetRolesResultToLoadSuccessProps(result.data));
         }),
         catchError((error) => {
            return of(roleRegisterAdapterActions.loadFailure(error));
         })
      );
   }
}
